import { Directions } from 'constants/directions';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import {
  StyledCard,
  CardHeaderStyled,
} from 'pages/Orders/OrdersShow/OrdersShowCommonInfo/OrdersShowCommonInfo.styled';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider, useShowController } from 'react-admin';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import {
  BooleanField,
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  NumberField,
  OrderStatusChipField,
  TextField,
} from 'shared/react-admin/Fields';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { Currency } from 'types/embedded/currency';
import { Order } from 'types/embedded/order';
import { OrderCustomData } from 'types/embedded/orderCustomData';

import { OrdersShowCommonInfoMainCopyButton } from './OrdersShowCommonInfoMainCopyButton';
import { OrdersShowCommonInfoMainIdField } from './OrdersShowCommonInfoMainIdField';
import { OrdersShowCommonInfoMainMerchantIdField } from './OrdersShowCommonInfoMainMerchantIdField';

export const OrdersShowCommonInfoMain = (): JSX.Element => {
  const translate = useTypedTranslate();

  const { hasResourceAccess } = usePermissions();
  const { record: order } = useShowController<Order>();
  const isWithdrawal = order?.direction.endsWith(Directions.Payout);

  return (
    <RecordContextProvider value={order}>
      <StyledCard>
        <CardHeaderStyled
          actionsProps={{ sx: { alignSelf: 'center' } }}
          appendActions={<OrdersShowCommonInfoMainCopyButton />}
          sx={{ paddingBottom: 0 }}
          title={translate('app.common.generalInformation')}
          titleSize="smallBold"
        />
        <CardContent sx={{ padding: '16px !important' }}>
          <CardTable tableBodyProps={{ sx: { '& td': { width: '159px' } } }}>
            <TableRowWrapper label={translate('app.table.idKubera')}>
              <OrdersShowCommonInfoMainIdField />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.idMerchant')}>
              <OrdersShowCommonInfoMainMerchantIdField />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.idProvider')}>
              <EmbeddedFunctionField
                emptyText={translate('app.common.no')}
                sourceField="providerReferenceId"
              />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.creationTime')}>
              <DateTimeField inline source="createdAt" />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.executionTime')}>
              <DateTimeField inline source="updatedAt" />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.status')}>
              <FunctionField
                render={(record: Order) => {
                  return (
                    <RecordContextProvider value={record.status}>
                      <OrderStatusChipField source="description" />
                    </RecordContextProvider>
                  );
                }}
              />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.amount')}>
              <NumberField
                options={{ minimumFractionDigits: 2 }}
                source="amount"
                sx={{
                  fontWeight: 700,
                }}
              />
            </TableRowWrapper>
            {hasResourceAccess(Resources.Currencies) && (
              <TableRowWrapper label={translate('app.table.currency')}>
                <EmbeddedFunctionField
                  render={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
                  sourceField="currency"
                />
              </TableRowWrapper>
            )}
            <TableRowWrapper label={translate('app.table.direction')}>
              <ReferenceField reference="directions" source="direction">
                <TextField source="description" />
              </ReferenceField>
            </TableRowWrapper>
            {hasResourceAccess('order_custom_datas') && (
              <TableRowWrapper label={translate('app.table.countryCode')}>
                <ReferenceField
                  link={false}
                  reference="order_custom_datas"
                  source="orderCustomData.@id"
                >
                  <FunctionField
                    render={(record: OrderCustomData) => {
                      return record?.customData?.country;
                    }}
                  />
                </ReferenceField>
              </TableRowWrapper>
            )}
            {isWithdrawal && (
              <TableRowWrapper label={translate('app.common.approved')}>
                <BooleanField source="capture" />
              </TableRowWrapper>
            )}
            {order?.orderProviderExternalError && (
              <TableRowWrapper label={translate('app.table.declineReason')}>
                <ReferenceField
                  link={false}
                  reference="orderProviderExternalErrors"
                  source="orderProviderExternalError"
                >
                  <ReferenceField
                    link={false}
                    reference="providerExternalErrors"
                    source="providerExternalError.@id"
                  >
                    <TextField source="reason" />
                  </ReferenceField>
                </ReferenceField>
              </TableRowWrapper>
            )}
          </CardTable>
        </CardContent>
      </StyledCard>
    </RecordContextProvider>
  );
};
